import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Button, Stack, Typography, Alert, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, CircularProgress
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageHeader from '../components/PageHeader';
import api from '../api';

// One JWKS document: its kids and whether the rotation overlap window is active.
const JwksCard = ({ title, jwks, highlightKid }) => (
  <Card>
    <CardContent>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="h6">{title}</Typography>
        <Chip
          size="small"
          color={jwks.overlap ? 'success' : 'default'}
          label={jwks.overlap ? `overlap window (${jwks.count} keys)` : `${jwks.count} key`}
        />
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {jwks.uri}
        </Typography>
      </Stack>
      {jwks.overlap && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Two keys published — current + previous. Tokens signed with the old key still verify during propagation.
        </Alert>
      )}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>kid</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>alg</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>use</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>kty</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jwks.keys.map((k) => (
            <TableRow key={k.kid} selected={highlightKid && k.kid === highlightKid}>
              <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{k.kid}</TableCell>
              <TableCell>{k.alg}</TableCell>
              <TableCell>{k.use}</TableCell>
              <TableCell>{k.kty}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const KeyRotation = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/jwks/inspect');
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const token = data?.m2mToken;

  return (
    <Box>
      <PageHeader
        title="Key Rotation"
        subtitle="Inspect the published gateway discovery JWKS (/jwks — user + M2M keys). After the rotation Lambda runs, the active key count grows (overlap window)."
        action={
          <Button variant="contained" startIcon={<AutorenewIcon />} onClick={refresh} disabled={loading}>
            Refresh
          </Button>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {token && (
        <Alert
          severity={token.available && token.presentInJwks && token.verified ? 'success' : 'warning'}
          icon={<VerifiedIcon />}
          sx={{ mb: 2 }}
        >
          {!token.available
            ? `No M2M token available to cross-check (${token.reason || 'unknown'}).`
            : token.presentInJwks && token.verified
              ? `Current M2M token (kid ${token.kid}) is published in the discovery JWKS and verifies.`
              : `Current M2M token (kid ${token.kid}) — presentInJwks: ${token.presentInJwks}, verified: ${token.verified}${token.reason ? ` (${token.reason})` : ''}.`}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>
      ) : data ? (
        <Stack spacing={3}>
          <JwksCard title="Discovery JWKS (/jwks)" jwks={data.jwks} highlightKid={token?.kid} />
        </Stack>
      ) : (
        <Alert severity="info">Click <strong>Refresh</strong> to load the published JWKS.</Alert>
      )}
    </Box>
  );
};

export default KeyRotation;
